/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Popup } from '@atlaskit/popup';
import { Box, Pressable, Text } from '@atlaskit/primitives';
import { cssMap, jsx, cx } from '@atlaskit/css';
import Link from '@atlaskit/link';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';

import { expValEquals } from '@confluence/feature-experiments';

import { useIsUrlWhiteboardCompetitor } from './useIsUrlWhiteboardCompetitor';

export const i18n = defineMessages({
	title: {
		id: 'experiment-prompt-whiteboard-competitor-link.prompt-popup.title',
		defaultMessage: 'Try Confluence whiteboards',
		description: 'Popup title to bring awareness to the whiteboards feature',
	},
	descriptionDefault: {
		id: 'experiment-prompt-whiteboard-competitor-link.prompt-popup.description-default',
		defaultMessage:
			'We noticed you pasted a {competitorName} link. Reduce context switching by using a Confluence whiteboard instead.',
		description:
			'Context which explains that whiteboards are a better alternative to the detected competitor as it keeps all work within Confluence',
	},
	descriptionImport: {
		id: 'experiment-prompt-whiteboard-competitor-link.prompt-popup.description-import',
		defaultMessage:
			'We noticed you pasted a {competitorName} link. Reduce context switching by importing your work to a Confluence whiteboard instead.',
		description:
			'Context which explains that whiteboards are a better alternative to the detected competitor as it keeps all work within Confluence and brings awareness of the Importer feature',
	},
	createButtonDefault: {
		id: 'experiment-prompt-whiteboard-competitor-link.prompt-popup.create-button-default',
		defaultMessage: 'Create a whiteboard',
		description: 'Button to create and redirect the user to a new whiteboard',
	},
	createButtonImport: {
		id: 'experiment-prompt-whiteboard-competitor-link.prompt-popup.create-button-import',
		defaultMessage: 'Import from {competitorName}',
		description:
			'Button to create and redirect the user to a new whiteboard as well as open the Importer',
	},
	dismissButton: {
		id: 'experiment-prompt-whiteboard-competitor-link.prompt-popup.dismiss-button',
		defaultMessage: 'Do not show again',
		description:
			'Button to close the popup and stop displaying the prompt icon and popup everywhere on the current page and not show it again',
	},
});

const styles = cssMap({
	pressable: {
		width: '24px',
		height: '24px',
		padding: token('space.050'),
		borderRadius: token('border.radius.100'),
	},
	pressableClosed: {
		backgroundColor: token('color.background.accent.purple.subtlest'),
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		},
	},
	pressableOpen: {
		backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		outline: `${token('space.025')} solid ${token('color.border.selected')}`,
		outlineOffset: token('space.negative.025'),
	},
	promptContainer: {
		display: 'inline-flex',
		alignItems: 'center',
		marginRight: token('space.025'),
		marginLeft: token('space.025'),
	},
	popupBox: {
		width: '360px',
		padding: token('space.300'),
	},
	popupDescription: {
		marginTop: token('space.100'),
		marginBottom: token('space.200'),
	},
});

const PromptComponent = ({ competitorName }: { competitorName: string }) => {
	const intl = useIntl();

	const isImport = competitorName?.toLowerCase().includes('miro'); // currently only Miro supports importer
	const descriptionText = intl.formatMessage(
		isImport ? i18n.descriptionImport : i18n.descriptionDefault,
		{ competitorName },
	);
	const createText = intl.formatMessage(
		isImport ? i18n.createButtonImport : i18n.createButtonDefault,
		{ competitorName },
	);

	return (
		<Box xcss={styles.popupBox} testId="competitor-link-prompt-popup">
			<Heading size="xsmall">{intl.formatMessage(i18n.title)}</Heading>
			<Box xcss={styles.popupDescription}>
				<Text>{descriptionText}</Text>
			</Box>
			<Link href="testUrl">{createText}</Link> {/* TODO: add logic to handle link */}
		</Box>
	);
};

export const PromptPopup = ({ competitorName }: { competitorName: string }) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => <PromptComponent competitorName={competitorName} />}
			trigger={(triggerProps) => (
				<Pressable
					{...triggerProps}
					onClick={() => setIsOpen(!isOpen)}
					xcss={cx(styles.pressable, isOpen ? styles.pressableOpen : styles.pressableClosed)}
					testId="competitor-link-prompt-icon"
				>
					<LightbulbIcon
						color={token('color.icon.discovery')}
						label={intl.formatMessage(i18n.title)}
					/>
				</Pressable>
			)}
		/>
	);
};

export const PromptPopupContainer = ({ sourceUrl }: { sourceUrl: string }) => {
	// TODO: add check for dismiss button being previously clicked
	const { competitorName } = useIsUrlWhiteboardCompetitor(sourceUrl);

	return competitorName &&
		expValEquals('prompt_whiteboard_competitor_link', 'cohort', 'experiment') ? (
		<Box xcss={styles.promptContainer}>
			<PromptPopup competitorName={competitorName} />
		</Box>
	) : null;
};
